import { useState, useEffect, useCallback, useRef } from "react";

import { supabase } from "../lib/supabase";

/**
 * Hook for managing user notifications with realtime updates.
 * Provides notifications list, unread count, and actions to mark read/delete.
 */
export const useNotifications = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);

  const userId = user?.id || user?.uid;

  // ===== FETCH NOTIFICATIONS =====
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount((data || []).filter((n) => !n.is_read).length);
    } catch (err) {
      console.error("[useNotifications] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // ===== MARK AS READ =====
  const markAsRead = useCallback(
    async (ids) => {
      if (!userId) return;

      try {
        if (ids === "all") {
          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("user_id", userId)
            .eq("is_read", false);

          if (error) throw error;

          setNotifications((prev) =>
            prev.map((n) => ({ ...n, is_read: true })),
          );
          setUnreadCount(0);
        } else {
          const idArray = Array.isArray(ids) ? ids : [ids];

          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("user_id", userId)
            .in("id", idArray);

          if (error) throw error;

          setNotifications((prev) =>
            prev.map((n) =>
              idArray.includes(n.id) ? { ...n, is_read: true } : n,
            ),
          );
          setUnreadCount((prev) => Math.max(0, prev - idArray.length));
        }
      } catch (err) {
        console.error("[useNotifications] Mark read error:", err);
      }
    },
    [userId],
  );

  // ===== DELETE NOTIFICATION =====
  const deleteNotification = useCallback(
    async (id) => {
      if (!userId) return;

      try {
        const { error } = await supabase
          .from("notifications")
          .delete()
          .eq("id", id)
          .eq("user_id", userId);

        if (error) throw error;

        setNotifications((prev) => {
          const removed = prev.find((n) => n.id === id);
          if (removed && !removed.is_read) {
            setUnreadCount((c) => Math.max(0, c - 1));
          }
          return prev.filter((n) => n.id !== id);
        });
      } catch (err) {
        console.error("[useNotifications] Delete error:", err);
      }
    },
    [userId],
  );

  // ===== CLEAR ALL NOTIFICATIONS =====
  const clearAll = useCallback(async () => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("user_id", userId);

      if (error) throw error;

      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error("[useNotifications] Clear all error:", err);
    }
  }, [userId]);

  // ===== REALTIME SUBSCRIPTION =====
  useEffect(() => {
    if (!userId) return;

    // Optimized: Defer notification fetch to not block initial render
    const scheduleFetch = () => fetchNotifications();
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(scheduleFetch, { timeout: 3000 });
    } else {
      setTimeout(scheduleFetch, 150);
    }

    // Subscribe to realtime inserts for this user
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotif = payload.new;
          setNotifications((prev) => [newNotif, ...prev]);
          setUnreadCount((prev) => prev + 1);
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [userId, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    deleteNotification,
    clearAll,
    refetch: fetchNotifications,
  };
};

export default useNotifications;
